import { useCallback, useEffect, useMemo, useState } from "react";
import type { Channel } from "./host";
import { logEvent } from "../telemetry";

/**
 * User-level disable lists (ST-04: per-skill enable/disable and per-module
 * toggles).
 *
 * Denylists the signed-in user maintains in Settings — skill keys under
 * Settings > Skills, module tile ids under Settings > Modules — persisted to
 * localStorage so they survive a pane reload (the ST-04 AC). Keyed per host so
 * disabling in Outlook does not change Word — the same per-channel rule pins
 * follow (usePinnedModules.ts).
 *
 * The lists only ever *hide within* the persona/workspace grant (ST-06):
 * consumers subtract them from an allow-list they already hold, so they can
 * never surface anything the workspace did not configure. They are
 * preferences, not a security boundary — they ride the client, and the only
 * person they govern is their owner.
 *
 * Skills are consumed by three surfaces, which is why the read is a plain
 * function rather than hook-only state:
 *   - Settings > Skills (useUserSkillPrefs) — the toggles themselves.
 *   - The slash registry (getDisabledSkillKeys) — filters the `/?` palette,
 *     typed-slash resolution and forced-skill lookup.
 *   - The chat send path (getDisabledSkillKeysList) — rides the request so
 *     the backend router never sees a disabled skill as a candidate.
 *
 * Modules are consumed by two React surfaces (Settings > Modules and
 * TaskPane's launcher/tab-bar assembly), which live in different subtrees, so
 * useUserModulePrefs re-reads on every write notification — a toggle flipped
 * in the Settings overlay reshapes the tab bar behind it immediately
 * ("disabling a module removes it from the launcher and tab bar entirely").
 */

const SKILLS_KEY_PREFIX = "aiva_disabled_skills";
const MODULES_KEY_PREFIX = "aiva_disabled_modules";

// Matches usePinnedModules.ts's STORAGE_REASON_* convention.
const STORAGE_REASON_READ_ERROR = "disabled_skills_read_error";
const STORAGE_REASON_WRITE_ERROR = "disabled_skills_write_error";

// Reports localStorage failures without disrupting the caller; the preference
// degrades to "nothing disabled" on read failure and a no-op on write failure.
function reportStorageError(
  reason: typeof STORAGE_REASON_READ_ERROR | typeof STORAGE_REASON_WRITE_ERROR,
): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: "skills-user-prefs", reason });
}

function storageKey(host: Channel | null): string {
  return `${SKILLS_KEY_PREFIX}:${host ?? "default"}`;
}

function moduleStorageKey(host: Channel | null): string {
  return `${MODULES_KEY_PREFIX}:${host ?? "default"}`;
}

function loadList(key: string): string[] {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.filter((k): k is string => typeof k === "string");
    }
  } catch (e) {
    console.debug("[userSkillPrefs] localStorage read failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_READ_ERROR);
  }
  return [];
}

function loadDisabled(host: Channel | null): string[] {
  return loadList(storageKey(host));
}

// Change listeners — the chat panel stays mounted (hidden) behind the
// Settings overlay, and useCommandList holds the palette's command list in
// React state, so a toggle flipped in Settings must push a notification for
// the palette to re-read the (freshly filtered) registry without a reload.
const _listeners = new Set<() => void>();

/** Subscribe to disable-list changes. Returns the unsubscribe function. */
export function subscribeUserSkillPrefs(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function saveList(key: string, values: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch (e) {
    console.debug("[userSkillPrefs] localStorage write failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_WRITE_ERROR);
  }
  for (const listener of _listeners) listener();
}

function saveDisabled(host: Channel | null, keys: string[]) {
  saveList(storageKey(host), keys);
}

/**
 * The user's disabled skill keys for `host`, read fresh from storage.
 * Set form for the registry's per-keystroke membership checks.
 */
export function getDisabledSkillKeys(host: Channel | null): ReadonlySet<string> {
  return new Set(loadDisabled(host));
}

/** Array form for the chat request payload. */
export function getDisabledSkillKeysList(host: Channel | null): string[] {
  return loadDisabled(host);
}

interface UseUserSkillPrefsReturn {
  /** Skill keys the user has switched off on this host. */
  disabledSkillKeys: ReadonlySet<string>;
  /** True when the user has switched this skill off. */
  isSkillDisabled: (skillKey: string) => boolean;
  /** Flip one skill's user-level state. */
  toggleSkill: (skillKey: string) => void;
  /** Set several skills at once — the group toggle's bulk on/off. */
  setSkillsDisabled: (skillKeys: string[], disabled: boolean) => void;
}

/* ── Modules (ST-04 per-module toggles) ─────────────────────────── */

/** The user's disabled module tile ids for `host`, read fresh from storage. */
export function getDisabledModuleIds(host: Channel | null): ReadonlySet<string> {
  return new Set(loadList(moduleStorageKey(host)));
}

interface UseUserModulePrefsReturn {
  /** Module tile ids the user has switched off on this host. */
  disabledModuleIds: ReadonlySet<string>;
  /** True when the user has switched this module off. */
  isModuleDisabled: (tileId: string) => boolean;
  /** Flip one module's user-level state. */
  toggleModule: (tileId: string) => void;
}

/**
 * Live view of the user's module disable list. Unlike the skills hook, this
 * one subscribes to write notifications: Settings > Modules and the TaskPane
 * launcher hold separate instances, and a toggle in the overlay must reshape
 * the launcher/tab bar behind it without a reload.
 */
export function useUserModulePrefs(host: Channel | null): UseUserModulePrefsReturn {
  const [disabled, setDisabled] = useState<string[]>(() => loadList(moduleStorageKey(host)));

  useEffect(() => {
    // Host changed or another instance wrote — re-read this host's bucket.
    setDisabled(loadList(moduleStorageKey(host)));
    return subscribeUserSkillPrefs(() => setDisabled(loadList(moduleStorageKey(host))));
  }, [host]);

  const toggleModule = useCallback(
    (tileId: string) => {
      const current = loadList(moduleStorageKey(host));
      const next = current.includes(tileId)
        ? current.filter((id) => id !== tileId)
        : [...current, tileId];
      saveList(moduleStorageKey(host), next);
    },
    [host],
  );

  const isModuleDisabled = useCallback((tileId: string) => disabled.includes(tileId), [disabled]);

  // Memoised so consumers can use the set as a hook dependency (TaskPane's
  // launcher memo) without recomputing on every render.
  const disabledModuleIds = useMemo(() => new Set(disabled), [disabled]);

  return { disabledModuleIds, isModuleDisabled, toggleModule };
}

export function useUserSkillPrefs(host: Channel | null): UseUserSkillPrefsReturn {
  const [disabled, setDisabled] = useState<string[]>(() => loadDisabled(host));

  const toggleSkill = useCallback(
    (skillKey: string) => {
      setDisabled((prev) => {
        const next = prev.includes(skillKey) ? prev.filter((k) => k !== skillKey) : [...prev, skillKey];
        saveDisabled(host, next);
        return next;
      });
    },
    [host],
  );

  const setSkillsDisabled = useCallback(
    (skillKeys: string[], disable: boolean) => {
      setDisabled((prev) => {
        const next = disable
          ? [...new Set([...prev, ...skillKeys])]
          : prev.filter((k) => !skillKeys.includes(k));
        saveDisabled(host, next);
        return next;
      });
    },
    [host],
  );

  const disabledSet = new Set(disabled);
  const isSkillDisabled = useCallback((skillKey: string) => disabled.includes(skillKey), [disabled]);

  return { disabledSkillKeys: disabledSet, isSkillDisabled, toggleSkill, setSkillsDisabled };
}
